/**
 * BaseInventoryTab - Tabla base de inventario reutilizable por todos los módulos
 *
 * @version 1.0
 * @updated 2026-02-23
 * @module core/layouts/panels/LeftPanel
 *
 * @description
 * Tabla genérica con: checkbox, eye, lupa (zoom), código, nombre.
 * Misma estética que TreesPanel pero sin datos reales — muestra filas vacías
 * hasta que el módulo conecte su fuente de datos.
 *
 * @remarks
 * Consume useLeftPanel para modo narrow/wide (collapsed).
 * Reutiliza las clases CSS de trees-panel.css (prefijo tp-).
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Eye, EyeOff, Search,
  ChevronUp, ChevronDown,
} from 'lucide-react';
import { useModuleVisibilityStore } from '../../../module-store/module-visibility-store';
import { useModuleStatsStore } from '../../../module-store/module-stats-store';
import type { ModuleId } from '../../../module-store/types';
import { TablePagination } from './TablePagination';
import './trees-panel.css';

// ─────────────────────────────────────────
// Types
// ─────────────────────────────────────────

export interface InventoryRow {
  id: string;
  code: string;
  name: string;
}

interface BaseInventoryTabProps {
  /** Título de la sección (ej: "Paradas", "Luminarias") */
  title: string;
  /** Filas de datos. Si vacío, muestra estado vacío */
  rows?: InventoryRow[];
  /** Mensaje cuando no hay datos */
  emptyMessage?: string;
  /** Prefijo de evento para visibilidad/zoom */
  eventPrefix?: string;
  /** ID del módulo al que pertenece este inventario (para control de visibilidad global) */
  moduleId?: ModuleId;
  /** Color del elemento en el mapa (solo para tipos mapeables) */
  elementColor?: string;
  /** Callback al cambiar el color del elemento */
  onElementColorChange?: (hex: string) => void;
  /** Callback al hacer clic en el código de un elemento */
  onCodeClick?: (id: string) => void;
  /** ID del elemento activamente seleccionado (se mostrará resaltado) */
  activeElementId?: string | null;
  /** Callback al pasar hover sobre una fila (id o null) */
  onRowHover?: (id: string | null) => void;
}

// ─────────────────────────────────────────
// Constants
// ─────────────────────────────────────────

const ROW_HEIGHT_PX = 26;

const COLOR_PRESETS = [
  '#ffffff', '#cbd5e1', '#94a3b8', '#64748b',
  '#334155', '#0f172a', '#6366f1', '#3b82f6',
  '#06b6d4', '#14b8a6', '#22c55e', '#84cc16',
  '#f59e0b', '#f97316', '#ef4444', '#ec4899',
  '#8b5cf6', '#a855f7', '#78716c', '#0ea5e9',
  '#d946ef', '#f43f5e', '#10b981', '#eab308',
];

type SortDir = 'asc' | 'desc';

// ─────────────────────────────────────────
// ColorPalette (portal, fixed position)
// ─────────────────────────────────────────

function ColorPalette({
  anchorRef,
  currentColor,
  onSelect,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLSpanElement | null>;
  currentColor: string;
  onSelect: (hex: string) => void;
  onClose: () => void;
}): JSX.Element | null {
  const paletteRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Position next to swatch
  useEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ top: rect.top + rect.height / 2, left: rect.right + 8 });
  }, [anchorRef]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        paletteRef.current && !paletteRef.current.contains(e.target as Node) &&
        anchorRef.current && !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [anchorRef, onClose]);

  if (!pos) return null;

  return createPortal(
    <div
      ref={paletteRef}
      className="tp-color-palette"
      style={{ top: pos.top, left: pos.left }}
    >
      {COLOR_PRESETS.map((hex) => (
        <span
          key={hex}
          className={`tp-color-preset${hex === currentColor ? ' active' : ''}`}
          style={{ backgroundColor: hex }}
          onClick={() => onSelect(hex)}
        />
      ))}
    </div>,
    document.body,
  );
}

// ─────────────────────────────────────────
// Component
// ─────────────────────────────────────────

export function BaseInventoryTab({
  title,
  rows: externalRows,
  emptyMessage = 'Sin elementos registrados',
  eventPrefix = 'element',
  moduleId,
  elementColor,
  onElementColorChange,
  onCodeClick,
  activeElementId,
  onRowHover,
}: BaseInventoryTabProps): JSX.Element {
  const [showPalette, setShowPalette] = useState(false);
  const swatchRef = useRef<HTMLSpanElement>(null);
  const { isModuleHidden } = useModuleVisibilityStore();
  const { updateElementType } = useModuleStatsStore();
  const rows = externalRows ?? [];

  // Reportar estadísticas cuando cambian las filas
  useEffect(() => {
    if (moduleId && title) {
      // Usar eventPrefix como typeKey único
      updateElementType(moduleId, eventPrefix, title, rows.length);
    }
  }, [moduleId, eventPrefix, title, rows.length, updateElementType]);

  // ── Visibility (eye toggle per element) ───
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());

  // ── Multi-select (checkbox) ───────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // ── Sort ──────────────────────────────────
  const [sortKey, setSortKey] = useState<string>('code');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // ── Pagination ────────────────────────────
  const [page, setPage] = useState(1);
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
  const bodyRef = useCallback((el: HTMLDivElement | null) => setBodyEl(el), []);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // ── Hover ─────────────────────────────────
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Notificar al padre cuando cambia el hover
  useEffect(() => {
    onRowHover?.(hoveredId);
  }, [hoveredId, onRowHover]);

  // ── ResizeObserver: rows per page ─────────
  useEffect(() => {
    if (!bodyEl) return;
    const observer = new ResizeObserver(([entry]) => {
      const h = entry.contentRect.height;
      if (h > 0) setRowsPerPage(Math.max(1, Math.floor(h / ROW_HEIGHT_PX)));
    });
    observer.observe(bodyEl);
    return () => observer.disconnect();
  }, [bodyEl]);

  // ── Escuchar cambios de visibilidad del módulo ────
  useEffect(() => {
    if (!moduleId) return; // Sin moduleId, no aplicar visibilidad global

    const handleModuleVisibilityChanged = (e: Event): void => {
      const { moduleId: changedModuleId, visible } = (e as CustomEvent<{ moduleId: string; visible: boolean }>).detail;
      if (changedModuleId !== moduleId) return;

      console.log('[BaseInventoryTab] Module visibility changed:', { moduleId, visible });

      if (visible) {
        // Módulo visible: mostrar todos los elementos
        setHiddenIds(new Set());
        // Emitir eventos para cada elemento
        rows.forEach(row => {
          window.dispatchEvent(new CustomEvent(`${eventPrefix}-visibility-toggle`, {
            detail: { elementId: row.id, visible: true },
          }));
        });
      } else {
        // Módulo oculto: ocultar todos los elementos
        setHiddenIds(new Set(rows.map(r => r.id)));
        // Emitir eventos para cada elemento
        rows.forEach(row => {
          window.dispatchEvent(new CustomEvent(`${eventPrefix}-visibility-toggle`, {
            detail: { elementId: row.id, visible: false },
          }));
        });
      }
    };

    window.addEventListener('module-visibility-changed', handleModuleVisibilityChanged);
    return () => {
      window.removeEventListener('module-visibility-changed', handleModuleVisibilityChanged);
    };
  }, [moduleId, rows, eventPrefix]);

  // ── Sincronizar hiddenIds con estado del módulo al cargar/actualizar datos ──
  useEffect(() => {
    if (moduleId && isModuleHidden(moduleId)) {
      // Si el módulo está oculto, ocultar todos los elementos
      const newHiddenIds = new Set(rows.map(r => r.id));
      setHiddenIds(newHiddenIds);

      // CRÍTICO: Emitir eventos de visibilidad para sincronizar con mapa
      rows.forEach(row => {
        window.dispatchEvent(new CustomEvent(`${eventPrefix}-visibility-toggle`, {
          detail: { elementId: row.id, visible: false },
        }));
      });
    }
  }, [rows, moduleId, isModuleHidden, eventPrefix]);

  // ── Sort handler ──────────────────────────
  const handleSort = (key: string): void => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  // ── Sorted rows ───────────────────────────
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const aVal = sortKey === 'code' ? a.code : a.name;
      const bVal = sortKey === 'code' ? b.code : b.name;
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, sortKey, sortDir]);

  // Ajustar página si rowsPerPage cambia
  useEffect(() => {
    const max = Math.ceil(sortedRows.length / rowsPerPage) || 1;
    if (page > max) setPage(max);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, sortedRows.length]);

  const totalPages = Math.ceil(sortedRows.length / rowsPerPage);

  const pageRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  // ── Visibility toggle ─────────────────────
  const handleToggleVisibility = (id: string): void => {
    setHiddenIds(prev => {
      const next = new Set(prev);
      const willHide = !next.has(id);
      if (willHide) { next.add(id); } else { next.delete(id); }
      window.dispatchEvent(new CustomEvent(`${eventPrefix}-visibility-toggle`, {
        detail: { elementId: id, visible: !willHide },
      }));
      return next;
    });
  };

  // ── Checkbox: individual ──────────────────
  const handleToggleSelect = (id: string): void => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  // ── Checkbox: select/deselect all on page ─
  const handleSelectAll = (): void => {
    const allSelected = pageRows.every(r => selectedIds.has(r.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        pageRows.forEach(r => next.delete(r.id));
      } else {
        pageRows.forEach(r => next.add(r.id));
      }
      return next;
    });
  };

  // ── Zoom ──────────────────────────────────
  const handleZoom = (row: InventoryRow): void => {
    window.dispatchEvent(new CustomEvent(`${eventPrefix}-zoom`, {
      detail: { elementId: row.id },
    }));
  };

  // ── Sort icon helper ──────────────────────
  const sortIcon = (key: string): React.ReactNode => {
    if (sortKey !== key) return null;
    return sortDir === 'asc'
      ? <ChevronUp size={9} className="tp-sort-icon" />
      : <ChevronDown size={9} className="tp-sort-icon" />;
  };

  // ── Derived state ─────────────────────────
  const hasSelection = selectedIds.size > 0;
  const allOnPageSelected = pageRows.length > 0 && pageRows.every(r => selectedIds.has(r.id));
  const someOnPageSelected = pageRows.some(r => selectedIds.has(r.id));
  const anySelectedVisible = hasSelection && [...selectedIds].some(id => !hiddenIds.has(id));

  // ── Render ────────────────────────────────
  return (
    <section className="tp-section" aria-label={`Inventario de ${title.toLowerCase()}`}>

      {/* ── Section header ── */}
      <header className={`tp-section-header${hasSelection ? ' has-selection' : ''}`}>
        {hasSelection ? (
          <>
            <span className="tp-selected-count">{selectedIds.size} seleccionados</span>
            <div className="tp-bulk-actions">
              <button
                className={`tp-bulk-btn${anySelectedVisible ? '' : ' all-hidden'}`}
                onClick={() => {
                  const anyVisible = [...selectedIds].some(id => !hiddenIds.has(id));
                  setHiddenIds(prev => {
                    const next = new Set(prev);
                    selectedIds.forEach(id => {
                      if (anyVisible) { next.add(id); } else { next.delete(id); }
                    });
                    return next;
                  });
                }}
                title={anySelectedVisible ? 'Ocultar en mapa' : 'Mostrar en mapa'}
              >
                {anySelectedVisible ? <Eye size={12} /> : <EyeOff size={12} />}
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="tp-section-title">{title}</span>
            <span className="tp-section-count">{rows.length.toLocaleString()}</span>
            {elementColor != null && (
              <span className="tp-color-swatch-wrapper">
                <span
                  ref={swatchRef}
                  className="tp-color-swatch"
                  style={{ backgroundColor: elementColor }}
                  onClick={() => setShowPalette(p => !p)}
                  title="Cambiar color en mapa"
                />
                {showPalette && <ColorPalette
                  anchorRef={swatchRef}
                  currentColor={elementColor}
                  onSelect={(hex) => { onElementColorChange?.(hex); setShowPalette(false); }}
                  onClose={() => setShowPalette(false)}
                />}
              </span>
            )}
          </>
        )}
      </header>

      {/* ── Empty ── */}
      {rows.length === 0 && (
        <div className="tp-state tp-state--empty">
          <p>{emptyMessage}</p>
        </div>
      )}

      {/* ── Table ── */}
      {rows.length > 0 && (
        <>
          {/* Header */}
          <div className={`tp-header tp-header--narrow`}>
            <input
              type="checkbox"
              className="tp-checkbox"
              checked={allOnPageSelected}
              ref={el => {
                if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected;
              }}
              onChange={handleSelectAll}
              title="Seleccionar todos en la página"
              aria-label="Seleccionar todos"
            />
            <span className="tp-eye-placeholder" />
            <span className="tp-zoom-placeholder" />
            <button
              className={`tp-col-header${sortKey === 'code' ? ' active' : ''}`}
              onClick={() => handleSort('code')}
              title="Ordenar por código"
            >
              Código {sortIcon('code')}
            </button>
            <button
              className={`tp-col-header tp-col-header--flex${sortKey === 'name' ? ' active' : ''}`}
              onClick={() => handleSort('name')}
              title="Ordenar por nombre"
            >
              Nombre {sortIcon('name')}
            </button>
          </div>

          {/* Body */}
          <div className="tp-body" ref={bodyRef}>
            {pageRows.map(row => {
              const isHidden = hiddenIds.has(row.id);
              const isSelected = selectedIds.has(row.id);
              const isHovered = hoveredId === row.id;
              const isActiveSelection = activeElementId != null && row.id === activeElementId;
              return (
                <div
                  key={row.id}
                  className={[
                    'tp-row tp-row--narrow',
                    isHidden ? 'hidden' : '',
                    isHovered ? 'hovered' : '',
                    isSelected ? 'selected' : '',
                    isActiveSelection ? 'active-selection' : '',
                  ].filter(Boolean).join(' ')}
                  onMouseEnter={() => setHoveredId(row.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <input
                    type="checkbox"
                    className="tp-checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleSelect(row.id)}
                    onClick={e => e.stopPropagation()}
                    aria-label={`Seleccionar ${row.code}`}
                  />
                  <button
                    className={`tp-eye-btn${isHidden ? ' hidden' : ''}`}
                    onClick={e => { e.stopPropagation(); handleToggleVisibility(row.id); }}
                    title={isHidden ? 'Mostrar en mapa' : 'Ocultar en mapa'}
                  >
                    {isHidden ? <EyeOff size={9} /> : <Eye size={9} />}
                  </button>
                  <button
                    className="tp-zoom-btn"
                    onClick={e => { e.stopPropagation(); handleZoom(row); }}
                    title="Acercarse en el mapa"
                  >
                    <Search size={9} />
                  </button>
                  {onCodeClick ? (
                    <button
                      className="tp-cell tp-cell--code tp-cell--clickable"
                      onClick={e => { e.stopPropagation(); onCodeClick(row.id); }}
                      title={`${row.code} - Clic para abrir propiedades`}
                    >
                      {row.code}
                    </button>
                  ) : (
                    <span className="tp-cell tp-cell--code" title={row.code}>
                      {row.code}
                    </span>
                  )}
                  <span className="tp-cell tp-cell--name" title={row.name}>
                    {row.name}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <TablePagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </section>
  );
}
